import {
  directionArrowRight,
  imgElementSvg,
} from '../../../components/shared/vector-images';
import { callEndpoint, telemetry } from '../../backend';
import {
  fitMapToLineLayerBounds,
  moveMapToFloatPlanTrip,
} from '../../utilities';
import { layerExists, setDataSource, sourceExists } from '../_helpers';

let mapReference;
let currentDisplayLineStrings = [];

const loadDirectionArrow = async () => {
  const arrow = await imgElementSvg(directionArrowRight(), {
    width: 90,
    height: 90,
    fillColor: '#FF5C39',
  });
  if (!mapReference.hasImage('loadedTripsDirectionArrow'))
    mapReference.addImage('loadedTripsDirectionArrow', arrow);
};

const addLayersIfRequired = () => {
  if (!sourceExists(mapReference, 'loadedTrips')) {
    mapReference.addSource('loadedTrips', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
  }
  if (!layerExists(mapReference, 'loadedTrips')) {
    mapReference.addLayer({
      id: 'loadedTrips',
      type: 'line',
      source: 'loadedTrips',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': '#FF5C39',
        'line-width': 6,
      },
    });
    mapReference.addLayer({
      id: 'loadedTripsArrowLayer',
      type: 'symbol',
      source: 'loadedTrips',
      layout: {
        'symbol-placement': 'line',
        'symbol-spacing': 100,
        'icon-allow-overlap': true,
        'icon-image': 'loadedTripsDirectionArrow',
        'icon-size': 0.33,
        visibility: 'visible',
      },
    });
  }
};

const layer = {
  displayName: 'Loaded Trips',
  synonyms: '',
  group: 'tileLayer',
  typeUrl: 'loaded-trips',
  shownByDefault: true,
  relativeDepth: 10,
  menuOptions: {
    visible: true,
    owningMenu: 'log-trips',
    order: 0,
  },
  icon: null,
};

const formatAsGeoJSONLineString = (locations, properties = {}) => ({
  type: 'geojson',
  data: {
    type: 'Feature',
    properties,
    geometry: {
      type: 'LineString',
      coordinates: locations,
    },
  },
});

const updateLayerGeoJSONData = async () => {
  const geojson = {
    type: 'FeatureCollection',
    features: currentDisplayLineStrings.map((linestring) => linestring.data),
  };

  setDataSource(mapReference, 'loadedTrips', geojson);
};

export const removeAllLoadedTrips = () => {
  currentDisplayLineStrings = [];

  updateLayerGeoJSONData();
};

export const loadRemoteTripDetailsBase = async (
  tripID,
  mapPadding,
  pointOffset,
) => {
  // If we have yet to receive a reference to the map object through the onInitialize function,
  // just return as we can't update the data on the layer if the layer doesn't exist yet.
  if (!mapReference) return;
  addLayersIfRequired();

  // Reset the loaded trips
  removeAllLoadedTrips();

  // Load the required trip
  const trip = await callEndpoint(callEndpoint.endpoints.getTripTrackPoints, {
    tripID,
  });

  telemetry(
    telemetry.DEBUG,
    `[loadedTrips.loadRemoteTripDetails] Received Trip: ${JSON.stringify(
      trip,
    )}`,
  );

  // Sort the trip points ready to add to map
  const sortedTrackPointsToShow =
    trip &&
    trip.trackPoints &&
    trip.trackPoints.map((point) => [point.longitude, point.latitude]);
  const { data } = formatAsGeoJSONLineString(sortedTrackPointsToShow, {
    tripID,
  });

  // Update the map with the sorted trip points
  currentDisplayLineStrings.push({
    tripID,
    data,
  });
  updateLayerGeoJSONData();

  const { coordinates } = data?.geometry;

  // Get the first trip point to use as a fallback map center
  let tripStartPoint;
  if (sortedTrackPointsToShow?.length > 0 && sortedTrackPointsToShow?.[0]) {
    tripStartPoint = {
      lat: sortedTrackPointsToShow?.[0]?.[1],
      lon: sortedTrackPointsToShow?.[0]?.[0],
    };
  }

  fitMapToLineLayerBounds(
    mapReference,
    coordinates,
    mapPadding,
    pointOffset,
    tripStartPoint,
    0,
  );
};

/**
 * Loads the trip onto the loaded trip map layer
 * @param {number} tripID - ID of the trip to load
 * @param {Object} mapPadding - padding values {top: 50, bottom: 50, left: 250, right: 250}
 * @param {Object} pointOffset - map center offset {xOffset: number, yOffset: number}
 * @return {void} Nothing
 */
export const loadRemoteTripDetails = async (
  tripID,
  mapPadding,
  pointOffset,
) => {
  // Sometimes the map hasn't loaded by the time we want to show the trip
  // This checks repeatedly until it is available
  if (mapReference) {
    await loadRemoteTripDetailsBase(tripID, mapPadding, pointOffset);
  } else {
    setTimeout(
      () => loadRemoteTripDetails(tripID, mapPadding, pointOffset),
      500,
    );
  }
};

export const removeLoadedTrip = async (tripID) => {
  currentDisplayLineStrings = currentDisplayLineStrings.filter(
    (i) => i.tripID !== tripID,
  );

  updateLayerGeoJSONData();
};

export const loadFloatPlanTripMap = (
  startWaypoint,
  endWaypoint,
  waypoints,
  mapPadding,
) => {
  // Sometimes the map hasn't loaded by the time we want to show the trip
  // This checks repeatedly until it is available
  if (mapReference) {
    const coordinates = [
      [startWaypoint.lon, startWaypoint.lat],
      [endWaypoint.lon, endWaypoint.lat],
      ...(waypoints || []).map((waypoint) => [
        waypoint?.latLon?.lon,
        waypoint?.latLon?.lat,
      ]),
    ];

    moveMapToFloatPlanTrip(mapReference, coordinates, mapPadding);
  } else {
    setTimeout(
      () =>
        loadFloatPlanTripMap(startWaypoint, endWaypoint, waypoints, mapPadding),
      500,
    );
  }
};

layer.onInitialize = (map) => {
  mapReference = map;
  addLayersIfRequired();

  loadDirectionArrow();
};

layer.onToggle = () => {};

export default layer;
